import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, post, put } from '../../../../../shared/api/apiService';
import { useApiEssentials } from '../../../../../shared/hooks/useApiEssentials';
import {
  WhitelistDomain,
  WhitelistDomainPage,
  WhitelistDomainPageRequest,
  WhitelistForm,
} from '../types';

const WHITELIST_ENDPOINT = '/white-list-domains';

export function useWhitelistDomains(
  params: WhitelistDomainPageRequest,
  options?: { enabled?: boolean },
) {
  const { apiUrl, getIdToken, isSignedIn } = useApiEssentials();
  const { enabled = true } = options ?? {};

  return useQuery<WhitelistDomainPage, Error>({
    queryKey: ['white-list-domains', params],
    queryFn: async () => {
      const idToken = await getIdToken();
      return post<WhitelistDomainPage>(
        `${WHITELIST_ENDPOINT}/page`,
        params,
        apiUrl,
        idToken,
      );
    },
    enabled: isSignedIn && enabled,
    retry: false,
  });
}

export function useWhitelistDomain(id?: string) {
  const { apiUrl, getIdToken, isSignedIn } = useApiEssentials();

  return useQuery<WhitelistDomain, Error>({
    queryKey: ['white-list-domains', id],
    queryFn: async () => {
      if (!id) {
        throw new Error('Whitelist domain id is required');
      }
      const idToken = await getIdToken();
      return get<WhitelistDomain>(`${WHITELIST_ENDPOINT}/${id}`, apiUrl, idToken);
    },
    enabled: isSignedIn && !!id,
    retry: false,
  });
}

export function useCreateWhitelistDomain() {
  const { apiUrl, getIdToken } = useApiEssentials();
  const queryClient = useQueryClient();

  return useMutation<WhitelistDomain, Error, WhitelistForm>({
    mutationKey: ['create-white-list-domain'],
    mutationFn: async payload => {
      const idToken = await getIdToken();
      return post<WhitelistDomain>(WHITELIST_ENDPOINT, payload, apiUrl, idToken);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['white-list-domains'] });
    },
  });
}

export function useDeleteWhitelistDomain() {
  const { apiUrl, getIdToken } = useApiEssentials();
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationKey: ['delete-white-list-domain'],
    mutationFn: async id => {
      const idToken = await getIdToken();
      return del<void>(`${WHITELIST_ENDPOINT}/${id}`, apiUrl, idToken);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['white-list-domains'] });
    },
  });
}

export function useUpdateWhitelistDomainActive() {
  const { apiUrl, getIdToken } = useApiEssentials();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { id: string; isActive: boolean }>({
    mutationKey: ['update-white-list-domain-active'],
    mutationFn: async ({ id, isActive }) => {
      const idToken = await getIdToken();
      return put<void>(
        `${WHITELIST_ENDPOINT}/${id}/active`,
        { active: isActive },
        apiUrl,
        idToken,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['white-list-domains'] });
    },
  });
}
